import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, ShieldCheck, ShieldAlert } from 'lucide-react';
import { DASH, authHeader } from '../api';
import { SectionCard, Spinner, EmptyState, Badge, fmtDate } from '../ui';

const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();

const HEALTH_LABELS = {
  pregnant: 'Pregnant',
  heart_or_bp: 'Heart / high blood pressure',
  heat_sensitivity: 'Heat sensitivity',
  medical_condition: 'Medical condition (physician supervision)',
  under_medical_care: 'Under medical care',
  none_of_above: 'None of the above',
};

const WaiverDetailModal = ({ waiverId, onClose }) => {
  const [waiver, setWaiver] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get(`${DASH}/waivers/${waiverId}`, { headers: authHeader() })
      .then((res) => setWaiver(res.data))
      .catch(() => setError('Could not load this waiver.'));
  }, [waiverId]);

  return (
    <div data-testid="waiver-detail-modal" className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl border border-gray-200 p-6 relative max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button data-testid="waiver-detail-close" onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1">
          <X size={18} />
        </button>
        {error && <div className="text-sm text-red-700">{error}</div>}
        {!waiver && !error && <Spinner label="Loading waiver…" />}
        {waiver && (
          <div className="space-y-4">
            <div>
              <h3 className="text-xl" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#0d1f15' }}>{waiver.full_name}</h3>
              <p className="text-sm text-[#5a7062]">{waiver.email} · {waiver.phone}</p>
              <p className="text-xs text-[#9aa69e] mt-1">Signed {fmtDate(waiver.signed_at)} · Expires {fmtDate(waiver.expires_at)}</p>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#5a7062] mb-2">Health Screening</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(waiver.health_screening || {}).filter(([, v]) => v).map(([k]) => (
                  <Badge key={k} tone={k === 'none_of_above' ? 'green' : 'gold'}>{HEALTH_LABELS[k] || k}</Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-green-700"><ShieldCheck size={14} /> Safety ack {waiver.safety_ack ? 'yes' : 'no'}</span>
              <span className="flex items-center gap-1.5 text-green-700"><ShieldCheck size={14} /> Liability ack {waiver.liability_ack ? 'yes' : 'no'}</span>
              <span className="flex items-center gap-1.5 text-green-700"><ShieldCheck size={14} /> Usage ack {waiver.usage_rules_ack ? 'yes' : 'no'}</span>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#5a7062] mb-2">Signature</div>
              {waiver.signature ? (
                <img data-testid="waiver-detail-signature" src={waiver.signature} alt="Signature" className="border border-[#f0ebe0] rounded-lg bg-[#faf7f0] max-h-32" />
              ) : (
                <p className="text-sm text-[#5a7062]">No signature on file.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const WaiversSection = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${DASH}/waivers`, { headers: authHeader() })
      .then((res) => setRows(res.data || []))
      .catch(() => setError('Could not load waivers.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner label="Loading waivers…" />;

  return (
    <div data-testid="wellness-admin-section-waivers" className="space-y-4">
      <div>
        <h2 className="text-2xl tracking-tight" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, color: '#0d1f15' }}>
          Waivers
        </h2>
        <p className="text-sm text-[#5a7062] mt-1">Signed Wellness Acknowledgment &amp; Intake Forms.</p>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <SectionCard testId="waivers-card">
        {rows.length === 0 ? (
          <EmptyState testId="waivers-empty" label="No waivers signed yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[#5a7062] border-b border-[#f0ebe0]">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Contact</th>
                  <th className="py-2 pr-4">Signed</th>
                  <th className="py-2 pr-4">Expires</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr
                    key={w.id}
                    data-testid={`waiver-row-${w.id}`}
                    onClick={() => setSelected(w.id)}
                    className="border-b border-[#f6f4ef] last:border-0 cursor-pointer hover:bg-[#faf7f0]"
                  >
                    <td className="py-3 pr-4 font-semibold text-[#0d1f15]">{w.full_name}</td>
                    <td className="py-3 pr-4">
                      <div>{w.email}</div>
                      <div className="text-xs text-[#5a7062]">{w.phone}</div>
                    </td>
                    <td className="py-3 pr-4">{fmtDate(w.signed_at)}</td>
                    <td className="py-3 pr-4">{fmtDate(w.expires_at)}</td>
                    <td className="py-3 pr-4">
                      {isExpired(w.expires_at) ? (
                        <Badge tone="red"><ShieldAlert size={11} className="inline mr-1" />Expired</Badge>
                      ) : (
                        <Badge tone="green">Active</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
      {selected && <WaiverDetailModal waiverId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default WaiversSection;
